import {
  ChangeRateCardOnProjectDocument,
  type ChangeRateCardOnProjectMutation,
  type ChangeRateCardOnProjectMutationVariables,
  GetProjectSettingsDocument,
  type GetProjectSettingsQuery,
  type GetProjectSettingsQueryVariables,
  RateCardsDocument,
  type RateCardsQuery,
  type RateCardsQueryVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { denominate } from "@src/utils/formatters";
import { NumberModel } from "@src/utils/models";
import { FieldState } from "formstate";
import { compact } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { ProjectSettingsModel } from "./models";

export class StoreProjectSettings {
  appStore: AppStore;
  @observable projectSettings?: ProjectSettingsModel;
  @observable rateCards: IOption[] = [];
  @observable rateCardsData: RateCardsQuery["projectRateCard"] = [];
  rateCardId = new FieldState<string | undefined | null>(undefined);
  exchangeRate = new FieldState(
    NumberModel.fromValue(1, EXCHANGE_RATE_DENOMINATION),
  );

  private rateCardsFetcher = new FetchHelper<
    RateCardsQuery,
    RateCardsQueryVariables
  >(RateCardsDocument);

  private projectSettingsFetcher = new FetchHelper<
    GetProjectSettingsQuery,
    GetProjectSettingsQueryVariables
  >(GetProjectSettingsDocument);

  private changeRateCardMutator = new MutationHelper<
    ChangeRateCardOnProjectMutation,
    ChangeRateCardOnProjectMutationVariables
  >(ChangeRateCardOnProjectDocument);

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
  }

  @computed
  get updatingRateCard() {
    return this.changeRateCardMutator.isLoading.value;
  }

  @computed
  get fetchingRateCards() {
    return this.rateCardsFetcher.isLoading.value;
  }

  static create(appStore: AppStore) {
    return new StoreProjectSettings(appStore);
  }

  @computed
  get selectedRateCard() {
    return this.rateCardsData.find((card) => card?.id === this.rateCardId.$);
  }

  @computed
  get showExchangeRateInput() {
    const currencyIso = this.selectedRateCard?.currency_iso;
    if (!currencyIso) return false;

    const workspaceSettings = this.appStore.workspaceStore.settings;
    if (currencyIso !== workspaceSettings?.currency.code) return true;

    return !!workspaceSettings?.currencyExchangeRatesStructured
      ?.find((rate) => rate.currency.code === currencyIso)
      ?.exchangeRates.some(
        (rate) =>
          Number(rate.exchange_rate) !==
          denominate(1, EXCHANGE_RATE_DENOMINATION),
      );
  }

  @action.bound
  async fetchRateCards(brandId: string) {
    const [data, error] = await this.rateCardsFetcher.fetch({
      brandId,
    });

    if (error || !data.projectRateCard) return;

    this.rateCardsData = data.projectRateCard;
    this.rateCards = compact(
      data.projectRateCard.map((card) =>
        card ? { value: card.id, label: card.name } : null,
      ),
    );
  }

  @action.bound
  async updateProjectsRateCard(newRateCardId: string) {
    if (!this.projectSettings?.id) return;

    await this.changeRateCardMutator.mutate({
      projectId: this.projectSettings.id,
      rateCardId: newRateCardId,
      exchangeRate: this.showExchangeRateInput
        ? this.exchangeRate.$.denominatedValue
        : denominate(1, EXCHANGE_RATE_DENOMINATION),
    });
  }

  @action.bound
  setExchangeRateFromProject(denominatedRate: number) {
    this.exchangeRate.onChange(
      NumberModel.fromDenomValue(denominatedRate, EXCHANGE_RATE_DENOMINATION),
    );
  }

  @action.bound
  async fetchProjectSettings() {
    if (!this.projectSettings?.id) return;

    const [data, error] = await this.projectSettingsFetcher.fetch({
      id: this.projectSettings.id,
    });

    if (error || !data.project) return;

    this.projectSettings = new ProjectSettingsModel(data.project);
    this.setExchangeRateFromProject(
      Number(data.project.rate_card_exchange_rate),
    );
  }
}
